// Servicio para conectar con Cloud Functions de Gemini
interface GenerateContentRequest {
  contentType: 'image';
  products: Array<{
    id: string;
    name: string;
    category: string;
    image?: string;
  }>;
  prompt?: string;
}

interface GenerateContentResponse {
  success: boolean;
  content: string; // Esta será la imagen en base64: data:image/png;base64,...
  type: 'image';
  products: Array<{
    id: string;
    name: string;
    category: string;
    image?: string;
    variants?: string[];
  }>;
  prompt: string;
  generatedAt: Date;
}

class GeminiAIService {
  private baseUrl: string;

  constructor() {
    // URL de las Cloud Functions desplegadas
    this.baseUrl = 'https://us-central1-zeta-ai-b480e.cloudfunctions.net';
  }

  /**
   * Generar contenido con Gemini 2.5 Flash Image
   */
  async generateContent(request: GenerateContentRequest): Promise<GenerateContentResponse> {
    try {
      console.log('🤖 Generando contenido con Gemini 2.5 Flash Image:', request);

      const functionName = 'generateImageReal';

      // Construir nombres de productos
      const productNames = request.products.map(p => p.name).join(', ');
      const productCategories = Array.from(new Set(request.products.map(p => p.category))).join(', ');
      
      // Obtener la imagen del primer producto
      const firstProductImage = request.products[0]?.image;
      
      console.log('📤 Datos del request:');
      console.log('   - Productos:', productNames);
      console.log('   - Categorías:', productCategories);
      console.log('   - Imagen del producto:', firstProductImage);
      console.log('   - Total productos:', request.products.length);

      // Llamar a la Cloud Function
      const requestBody = {
        productName: productNames,
        style: 'rioplatense-professional',
        productImageUrl: firstProductImage || null,
        customInstructions: request.prompt || null // Agregar instrucciones del cliente
      };
      
      console.log('🚀 Enviando request a Cloud Function:', requestBody);
      
      const response = await fetch(`${this.baseUrl}/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // ✅ VERIFICAR respuesta de la API
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error HTTP:', response.status, response.statusText);
        console.error('❌ Detalle:', errorText);
        throw new Error(`Error en la API: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      console.log('📦 Respuesta completa de Cloud Function:', result);
      console.log('🔍 Tipo de imageUrl:', typeof result.imageUrl);
      console.log('🔍 imageUrl primeros 100 chars:', result.imageUrl?.substring(0, 100));
      console.log('🔍 ¿Es placeholder?', result.imageUrl?.includes('placeholder'));
      console.log('🔍 ¿Es base64?', result.imageUrl?.startsWith('data:image'));
      
      // ✅ VALIDAR que la respuesta tenga una imagen real
      if (!result.success) {
        console.error('❌ Cloud Function reportó error:', result.error);
        throw new Error(result.error || 'Error desconocido en Cloud Function');
      }

      if (!result.imageUrl) {
        console.error('❌ No se recibió imageUrl en la respuesta');
        throw new Error('La Cloud Function no devolvió una imagen');
      }

      // ✅ VERIFICAR que NO sea un placeholder
      if (result.imageUrl.includes('placeholder') || result.imageUrl.includes('via.placeholder')) {
        console.error('❌ Cloud Function devolvió un placeholder en lugar de imagen real');
        console.error('❌ URL recibida:', result.imageUrl);
        throw new Error('Gemini no generó una imagen real. Verifica los logs de Cloud Functions.');
      }

      // ✅ VERIFICAR que sea una imagen base64 válida
      if (!result.imageUrl.startsWith('data:image')) {
        console.error('❌ La URL no es una imagen base64 válida');
        console.error('❌ URL recibida:', result.imageUrl);
        throw new Error('La respuesta no contiene una imagen base64 válida de Gemini');
      }

      console.log('✅ Imagen real de Gemini recibida correctamente');
      console.log('✅ Tamaño de la imagen:', result.imageUrl.length, 'caracteres');

      return {
        success: true,
        content: result.imageUrl, // data:image/png;base64,iVBORw0KG...
        type: 'image',
        products: request.products.map(p => ({
          ...p,
          variants: []
        })),
        prompt: result.prompt || '',
        generatedAt: new Date()
      };

    } catch (error) {
      console.error('❌ Error generando contenido:', error);
      
      // NO usar fallback - lanzar el error para que el usuario lo vea
      throw error;
    }
  }

  /**
   * Verificar si las Cloud Functions están disponibles
   */
  async checkAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/generateImageReal`, {
        method: 'OPTIONS', // Preflight CORS
      });
      
      return response.ok;
    } catch (error) {
      console.warn('⚠️ Cloud Functions no disponibles:', error);
      return false;
    }
  }
}

// Crear instancia singleton
const geminiAIService = new GeminiAIService();

export default geminiAIService;
export type { GenerateContentRequest, GenerateContentResponse };